import * as React from "react";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { HotelsProps } from "./typings";
import { inject, observer } from "mobx-react";
import { HotelCard } from "./components/HotelCard";
import { SeeAllContentLink } from "./components/SeeAllContentLink";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { UitkCardTextSegment } from "node_modules/uitk-react-cards";
import { CardContentReviewsPriceProposition } from "components/flexComponents/Hotels/components/CardContentReviewsPriceProposition";
import { SeeAllButton } from "components/flexComponents/Hotels/components/SeeAllButton";
import { LegalText } from "components/flexComponents/Hotels/components/LegalText";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { HotelInterruption } from "./components/HotelInterruption/HotelInterruption";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { HotelAllInclusiveMessage } from "./components/HotelAllInclusiveMessage/HotelAllInclusiveMessage";
import { HotelPoolAffinityFilters } from "./components/HotelAffinityFilters/HotelPoolAffinityFilters";
import { LoyaltySignup } from "./components/LoyaltySignup/LoyaltySignup";
import { HotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import { HotelStarRatingAffinityFilters } from "components/flexComponents/Hotels/components/HotelAffinityFilters/HotelStarRatingAffinityFilters";
import { classNames } from "uitk-react-utils";

/**
 * This component is the main overview of a property and gives basic information
 * to users in the hopes of getting them to click and purchase nights in it.
 *
 * This also handles mobile and desktop views.
 */
const HotelsComponent: React.FC<HotelsProps> = inject("hotels")((props: HotelsProps) => {
  const { flexComponent, context } = props;
  const keyHelper = new ItemKeyHelper("hotels");
  const model = flexComponent.model;
  const hotels: Hotel[] = model.hotels;

  if (!flexComponent || !model || !hotels || hotels.length === 0 || !props.hotels) {
    return null;
  }

  const affinityDescription =
    context && context.searchContext && context.searchContext.affinity && context.searchContext.affinity.description;
  const affinityTitle = model.view === "affinity" ? "affinityTitle" : "";
  const locationId = model.destinationId ?? context?.searchContext?.location?.id;

  const hotelsOnMap = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap;
  const isAffinityFiltersEnabled = isVariantEnabled(context, Experiment.Flex_Blossom_Affinity_Pool_Filters);
  const showSeeAllButton = isAffinityFiltersEnabled && hotels.length === 1;
  const { showSuggestedFilters, showMoreStrategy } = model;
  const addTopPaddingLegalText = !showSuggestedFilters && showMoreStrategy !== "button" && hotels.length > 1;

  React.useEffect(() => {
    if (props.hotels && hotels && hotels.length > 0) {
      if (hotelsOnMap) {
        props.hotels.fetchMapHotelsById(hotels, props.context, locationId);
      }
    }
  }, []);

  return (
    <HotelsContext.Provider value={{ context, flexComponent }}>
      <LoyaltySignup variant={2} />
      <div
        className={`Hotels all-cell-1-1 uitk-cell all-b-padding-three ${model.showRedPrices ? "hotelPricingApac" : ""}`}
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
      >
        {model.title && <h2 className={`uitk-type-heading-600 all-t-padding-three ${affinityTitle}`}>{model.title}</h2>}
        <HotelAllInclusiveMessage context={context} seeMoreMessage={model.affinityMessages?.seeMoreMessage} />
        <Viewport>
          <ViewMedium>
            <>
              {hotels.map((hotel, index) => (
                <Result
                  key={keyHelper.next()}
                  hotel={hotel}
                  index={index}
                  isMobile={false}
                  locationId={locationId}
                  hotelsProps={props}
                  cardContent={renderCardContent}
                  isAffinityFiltersEnabled={isAffinityFiltersEnabled}
                />
              ))}
            </>
          </ViewMedium>
          <ViewSmall>
            <>
              <SeeAllContentLink model={model} context={context} affinityDescription={affinityDescription} />
              {hotels.map((hotel, index) => (
                <Result
                  key={keyHelper.next()}
                  hotel={hotel}
                  index={index}
                  isMobile={true}
                  locationId={locationId}
                  hotelsProps={props}
                  cardContent={renderCardContent}
                  isAffinityFiltersEnabled={isAffinityFiltersEnabled}
                />
              ))}
            </>
          </ViewSmall>
        </Viewport>
        {showSeeAllButton ? null : <SeeAllButton />}
        <HotelStarRatingAffinityFilters />
        <LegalText className={addTopPaddingLegalText ? "all-t-padding-three" : ""} />
      </div>
    </HotelsContext.Provider>
  );
});

export const Hotels = observer(HotelsComponent);
export default Hotels;

interface ResultProps {
  hotel: Hotel;
  index: number;
  isMobile: boolean;
  locationId: string;
  hotelsProps: HotelsProps;
  cardContent: CardContentFunc;
  isAffinityFiltersEnabled?: boolean;
}

const Result: React.FC<ResultProps> = ({
  hotel,
  index,
  isMobile,
  locationId,
  hotelsProps: {
    context,
    flexComponent: { model },
    l10n,
  },
  cardContent,
  isAffinityFiltersEnabled,
}) => {
  const isAffinityView = model.view === "affinity";
  const hotelCardClasses = classNames("all-t-margin-three", isAffinityView && "affinityHotelCard");
  const hotelCard = (
    <HotelCard
      hotel={hotel}
      locationId={locationId}
      index={index + 1}
      callToActionHotelName={model.callToActionHotelName}
      context={context}
      CardContent={cardContent(hotel, isMobile, model, context)}
      className={hotelCardClasses}
      isMobile={isMobile}
      showGuestRating={model.showGuestRating}
      displayBadges={true}
      withBorder={isAffinityView}
    />
  );

  const loyaltyAuth = index === 3 && <LoyaltySignup classNames="all-t-margin-three" context={context} variant={1} />;
  const affinityFilters = (index === 0 || model.hotels.length === 1) && isAffinityFiltersEnabled && (
    <HotelPoolAffinityFilters />
  );

  if (model.alternativeHotelsOptions && model.alternativeHotelsOptions.position === index) {
    return (
      <>
        <HotelInterruption {...model.alternativeHotelsOptions} isMobile={isMobile} l10n={l10n} />
        {loyaltyAuth}
        {hotelCard}
        {affinityFilters}
      </>
    );
  }

  return (
    <>
      {loyaltyAuth}
      {hotelCard}
      {affinityFilters}
    </>
  );
};

type CardContentFunc = (
  hotel: Hotel,
  isMobile: boolean,
  model: HotelsFlexModuleResult,
  context: ExtendedContextStore
) => () => JSX.Element;

const renderCardContent: CardContentFunc = (hotel, isMobile, model, context) => () => (
  <UitkCardTextSegment className="first">
    <CardContentReviewsPriceProposition
      hotel={hotel}
      locale={context.locale}
      locationName={model.locationName}
      context={context}
      isMobile={isMobile}
      showDistance={model.showDistance}
      showAddress={model.showAddress}
      showGetPriceIfNoPrice={model.showGetPriceIfNoPrice}
      shouldUseImperialDistanceUnits={model.shouldUseImperialDistanceUnits}
      addressDistanceView={model.addressDistanceView}
    />
  </UitkCardTextSegment>
);
